/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useState} from 'react';
import {Spinner} from 'react-bootstrap';
import toast from 'react-hot-toast';

import {usePostRequest} from '../../../../../api/helper';
import {setIsAddStudyDialogOpen} from '../../../../../redux/ducks/CoverageRequirements/slice';
import {useAppDispatch, useAppSelector} from '../../../../../redux/hooks';
import ArgosButton from '../../../../components/Buttons/ArgosButton';
import ArgosDialogHeader from '../../../../components/Dialogs/ArgosDialogHeader';
import ArgosMenuItem from '../../../../components/Form/Select/ArgosMenuItem';
import ArgosSelect from '../../../../components/Form/Select/ArgosSelect';
import ArgosTextField from '../../../../components/Form/TextField/ArgosTextField';
import {usersDataEndpoint} from '../../constants';
import {Container, fieldHeight, fieldWidth, Header, LoaderWrapper, User} from './constants';
import {
  getIsUserAdmin,
  hasThisAccessLevelToRouteKey,
  useHasWriteAccess
} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import {EUserPermissionAccess} from "../../../../../redux/ducks/AccessControl/types";
import {Checkbox, FormControl, IconButton, InputLabel} from '@mui/material';
import {EArgosRouteKeys} from '../../../../../Routes/constants';
import CloseIcon from '@mui/icons-material/Close';
import ArgosFlexbox from '../../../../components/Containers/ArgosFlexbox';
import FormControlLabel from "@mui/material/FormControlLabel"
import {selectCurrentUser} from "../../../../../redux/ducks/AccessControl/slice";

function convertUTCDateToLocalDate(date: any) {
  var newDate = date; //We are already in UTC
  var offset = date.getTimezoneOffset() / 60; //offset in hours
  var hours = date.getHours();

  newDate.setHours(hours - offset);

  return newDate;
}

const AddStudyDialog = (props: any) => {

  const fetchHandle = usePostRequest(usersDataEndpoint)
  const dispatch = useAppDispatch()
  const hasWriteAccess = useHasWriteAccess()

  const [userData, setUserData] = useState([])
  const [studyName, setStudyName] = useState("")
  const [studyDescription, setStudyDescription] = useState("")
  const [studyOwner, setStudyOwner] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean | null>(null)
  const studyPlanOfRecord: boolean = false
  const studyArchive: boolean =false

  const onClickCancel = () => {
    dispatch(setIsAddStudyDialogOpen(false))
  }

  const onClickAddStudy = () => {
    const timeStamp = Date.now()
    const reqBody = {
      "id": `${studyOwner}_${timeStamp}`,
      "name": studyName,
      "owner": studyOwner,
      "description": studyDescription,
      "status": "New",
      "isPlanOfRecord": studyPlanOfRecord,
      "isStudyArchive": studyArchive
    }
    props.addStudy(reqBody)
    dispatch(setIsAddStudyDialogOpen(false))
  }

  function handleStudyOwnerChange(e: any) {
    setStudyOwner(e.target.value)
  }

  const fetchData = async () => {
    fetchHandle().then((userData) => {
      const filteredUsers: any = userData.filter((userInfo: any) => hasThisAccessLevelToRouteKey(EArgosRouteKeys.COVERAGE_REQUIREMENTS, userInfo, EUserPermissionAccess.READ_WRITE))
      setUserData(filteredUsers)
    }).catch(err => {
      console.log(err)
      toast.error("Failed to load user data")
      setIsLoading(false);
    })
    setIsLoading(false)
  }

  useEffect(() => {
    setIsLoading(true)
    fetchData()
  }, [])

  return (
    <Container>
      <ArgosDialogHeader>
        <ArgosFlexbox style={{justifyContent: 'space-between', alignItems: 'center'}}>
          <Header>
            <div style={{
              color: 'white',
              paddingLeft: 5,
              fontSize: 14
            }}>Add Study
            </div>
          </Header>
          <IconButton onClick={onClickCancel} sx={{color: 'white', padding: 0, fontSize: 'small'}}> 
            <CloseIcon />
          </IconButton>
        </ArgosFlexbox>
      </ArgosDialogHeader>
      {isLoading ?
        <LoaderWrapper>
          <Spinner animation={'border'} style={{color: 'grey'}}/>
        </LoaderWrapper>
        : hasWriteAccess && <div style={{
        padding: 5,
        alignSelf: 'center',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        alignItems: 'flex-start',
        gap: 20
      }}>
        <ArgosTextField
          required
          onChange={(e: any) => setStudyName(e.target.value)}
          value={studyName}
          name="study_name"
          sx={{width: fieldWidth, height: fieldHeight}}
          type="text"
          id="outlined-basic"
          label="Name"
        />
        <ArgosTextField
          multiline
          rows={4}
          required
          onChange={(e: any) => setStudyDescription(e.target.value)}
          value={studyDescription}
          name="study_description"
          sx={{width: fieldWidth, height: 2 * fieldHeight}}
          type="text"
          id="outlined-basic"
          label="Description"
        />
        <FormControl variant="standard" sx={{width: fieldWidth, height: fieldHeight}}>
          <InputLabel id="study_owner">Owner</InputLabel>
          <ArgosSelect
            required
            label="Owner"
            type='text'
            value={studyOwner}
            id="study_owner"
            onChange={(e) => handleStudyOwnerChange(e)}
            sx={{width: fieldWidth, height: fieldHeight}}
          >
            {
              userData.sort((a: User, b: User) =>
                a?.lastName.localeCompare(b.lastName) ||
                a?.firstName.localeCompare(b.firstName)
              ).map((user: User) =>
                <ArgosMenuItem
                  value={user.id}
                >
                  {user?.firstName + " " + user?.lastName ?? user.id}
                </ArgosMenuItem>)
            }
          </ArgosSelect>
        </FormControl>
      </div>}
      <div style={{alignSelf: 'center', display: 'flex', gap: 10, marginTop: 10}}>
        <ArgosButton
          onClick={onClickCancel}
        >
          Cancel
        </ArgosButton>
        <ArgosButton
          onClick={onClickAddStudy}
          disabled={studyName === "" || studyDescription === "" || studyOwner === "" || studyOwner === null}
        >
          Add Study
        </ArgosButton>
      </div>
    </Container>
  )
}

export default AddStudyDialog
